import type { DrrBucket, DrrViewRow } from '@api/inventory';
import type { ProductRelationEdge } from '@api/products';

import type {
    FinishedGoodsPlanningRow,
    FinishedGoodsSnapshotRow,
} from './inventory';

/**
 * Break-down behaviour for the two Inventory grid row shapes.
 *
 * Same three-bucket split as `replenishment-planning/explosion.ts` — quantities scale by the
 * edge quantity and add, ratios are recomputed from those quantities afterwards, identity
 * fields belong to the surviving SKU. What is different here is that both row shapes carry an
 * ARRAY of period or warehouse cells, each with its own ratio, so the recompute has to happen
 * per cell.
 *
 * The formulas deliberately mirror `buildSnapshotGroupRow` / `buildPlanningGroupRow` in
 * `inventory.tsx`, which already solve the identical problem for the rollup rows. Two
 * implementations of "what is this group's DoH" that disagreed would be worse than either.
 */

/**
 * A planning row mid-explosion.
 *
 * The weekly DoH is a ratio whose denominator is not on the row — only `opening` and `doh`
 * are. So the demand rate behind each week is backed out per contributor (`opening ÷ doh`),
 * accumulated here, and divided back at the end. It cannot be derived from the target after
 * the fact: by then `opening` has already moved and `doh` is stale, which would make the
 * result depend on how many parents happened to land first.
 */
type PlanningScratch = FinishedGoodsPlanningRow & { __weekRates?: number[] };

/** The demand rate a week implies, or 0 when the week reports no cover to divide by. */
const impliedWeekRate = (opening: number, doh: number | null | undefined): number =>
    doh && doh > 0 ? (opening || 0) / doh : 0;

/** Below this many days of cover a week is flagged — the same threshold the rollup uses. */
const SAFETY_DAYS = 14;

/* ── Snapshot ──────────────────────────────────────────────────────────────── */

export const cloneSnapshotRow = (
    row: FinishedGoodsSnapshotRow,
): FinishedGoodsSnapshotRow => ({
    ...row,
    warehouses: row.warehouses.map((warehouse) => ({ ...warehouse })),
});

export function addScaledSnapshotRow(
    target: FinishedGoodsSnapshotRow,
    source: FinishedGoodsSnapshotRow,
    quantity: number,
): void {
    target.totalInventory += (source.totalInventory || 0) * quantity;
    target.consumptionPerDay += (source.consumptionPerDay || 0) * quantity;

    // Null means "this merchant does not track on-hold", which is different from zero. A
    // contributor that tracks it makes the merged row track it too.
    if (source.onHoldStock !== null) {
        target.onHoldStock = (target.onHoldStock ?? 0) + source.onHoldStock * quantity;
    }

    // Same null-means-no-reading rule: only the channel views report in-transit at all.
    if (source.inTransit !== null) {
        target.inTransit = (target.inTransit ?? 0) + source.inTransit * quantity;
    }
    if (source.scheduled !== null) {
        target.scheduled = (target.scheduled ?? 0) + source.scheduled * quantity;
    }

    // Same rule again: null until some contributor's channel actually reports the figure.
    if (source.warehouseTransfer !== null) {
        target.warehouseTransfer =
            (target.warehouseTransfer ?? 0) + source.warehouseTransfer * quantity;
    }

    // Matched on warehouse NAME, which is what the column headers are keyed on. A parent
    // stocked in a warehouse the child is not simply adds a column.
    for (const warehouse of source.warehouses) {
        const existing = target.warehouses.find((w) => w.name === warehouse.name);
        if (existing) {
            existing.quantity += (warehouse.quantity || 0) * quantity;
            if (warehouse.onHoldStock !== null) {
                existing.onHoldStock =
                    (existing.onHoldStock ?? 0) + warehouse.onHoldStock * quantity;
            }
            if (warehouse.inTransit !== null) {
                existing.inTransit =
                    (existing.inTransit ?? 0) + warehouse.inTransit * quantity;
            }
            if (warehouse.scheduled !== null) {
                existing.scheduled = (existing.scheduled ?? 0) + warehouse.scheduled * quantity;
            }
            if (warehouse.warehouseTransfer !== null) {
                existing.warehouseTransfer =
                    (existing.warehouseTransfer ?? 0) + warehouse.warehouseTransfer * quantity;
            }
            continue;
        }
        target.warehouses.push({
            ...warehouse,
            quantity: (warehouse.quantity || 0) * quantity,
            onHoldStock:
                warehouse.onHoldStock === null ? null : warehouse.onHoldStock * quantity,
            inTransit:
                warehouse.inTransit === null ? null : warehouse.inTransit * quantity,
            warehouseTransfer:
                warehouse.warehouseTransfer === null
                    ? null
                    : warehouse.warehouseTransfer * quantity,
        });
    }
}

/**
 * DoH/MoH re-derived from the merged inventory and consumption.
 *
 * Curried over the merchant's inventory period because the displayed figure is in months for
 * a MONTH merchant — exactly as `buildSnapshotGroupRow` does it. Memoise the returned
 * function in the view; `useSkuExplosion` keys its work on callback identity.
 */
export const makeSnapshotRecompute =
    (inventoryPeriod: 'DAY' | 'MONTH') =>
    (row: FinishedGoodsSnapshotRow): void => {
        row.daysOnHand =
            row.consumptionPerDay > 0
                ? row.totalInventory /
                  row.consumptionPerDay /
                  (inventoryPeriod === 'MONTH' ? 30 : 1)
                : null;
    };

export function makeEmptySnapshotRow(
    edge: ProductRelationEdge,
): FinishedGoodsSnapshotRow {
    return {
        productId: edge.childId,
        skuCode: edge.childSku ?? edge.childId,
        description: edge.childName ?? '',
        totalInventory: 0,
        // Zero rather than null: this row is about to accumulate, and null would make the
        // first `+=` discard whatever the parent contributed.
        onHoldStock: 0,
        // Null rather than zero here, unlike on-hold: the accumulator above lifts it to a
        // number the moment any contributor reports one, and outside a channel view nobody
        // does — a zero would claim a reading the server never gave.
        inTransit: null,
        scheduled: null,
        warehouseTransfer: null,
        consumptionPerDay: 0,
        daysOnHand: null,
        warehouses: [],
        category: edge.childCategory,
        tier: edge.childTier,
        productGroup: edge.childProductGroup,
        isActive: null,
    };
}

/* ── Planning / Pipeline ───────────────────────────────────────────────────── */

export const clonePlanningRow = (row: FinishedGoodsPlanningRow): PlanningScratch => ({
    ...row,
    weeks: row.weeks.map((week) => ({
        ...week,
        incomingLegs: { ...week.incomingLegs },
    })),
    // Seeded from the row's OWN weeks, so a row that absorbs nothing still recomputes to the
    // cover it already showed rather than to null.
    __weekRates: row.weeks.map((week) => impliedWeekRate(week.opening, week.doh)),
});

export function addScaledPlanningRow(
    target: PlanningScratch,
    source: FinishedGoodsPlanningRow,
    quantity: number,
): void {
    target.totalInventory += (source.totalInventory || 0) * quantity;

    const rates = target.__weekRates ?? [];

    source.weeks.forEach((week, index) => {
        // A parent with a longer horizon than the child extends it. The child's earlier weeks
        // are already correct; the new tail starts from the parent's contribution alone.
        let targetWeek = target.weeks[index];
        if (!targetWeek) {
            targetWeek = {
                label: week.label,
                opening: 0,
                consumption: 0,
                inTransit: 0,
                incomingLegs: { inTransit: 0, inProductionPo: 0, inProductionWo: 0 },
                doh: null,
            };
            target.weeks[index] = targetWeek;
        }

        targetWeek.opening += (week.opening || 0) * quantity;
        targetWeek.consumption += (week.consumption || 0) * quantity;
        targetWeek.inTransit += (week.inTransit || 0) * quantity;
        targetWeek.incomingLegs.inTransit += (week.incomingLegs?.inTransit || 0) * quantity;
        targetWeek.incomingLegs.inProductionPo +=
            (week.incomingLegs?.inProductionPo || 0) * quantity;
        targetWeek.incomingLegs.inProductionWo +=
            (week.incomingLegs?.inProductionWo || 0) * quantity;

        // Scaling the rate by the same quantity as the opening is what keeps cover
        // scale-invariant: two of the child per parent doubles both, and the ratio holds.
        rates[index] = (rates[index] ?? 0) + impliedWeekRate(week.opening, week.doh) * quantity;
    });

    target.__weekRates = rates;
}

export function recomputePlanningRow(row: PlanningScratch): void {
    const rates = row.__weekRates ?? [];
    row.weeks = row.weeks.map((week, index) => {
        const rate = rates[index] ?? 0;
        const doh = rate > 0 ? week.opening / rate : null;
        return { ...week, doh, belowSafety: (doh || 0) < SAFETY_DAYS };
    });
    delete row.__weekRates;
}

export function makeEmptyPlanningRow(
    edge: ProductRelationEdge,
): FinishedGoodsPlanningRow {
    return {
        productId: edge.childId,
        skuCode: edge.childSku ?? edge.childId,
        description: edge.childName ?? '',
        totalInventory: 0,
        // Grown by `addScaled` to whatever horizon the contributing parents have.
        weeks: [],
        category: edge.childCategory,
        tier: edge.childTier,
        productGroup: edge.childProductGroup,
        isActive: null,
    };
}

/** Stable identities — see the note on `getHealthRowId`. */
export const getSnapshotRowId = (row: FinishedGoodsSnapshotRow): string => row.productId;
export const getPlanningRowId = (row: FinishedGoodsPlanningRow): string => row.productId;

/* ── DoH view (per-channel DRR / stock / cover) ─────────────────────────────── */

/**
 * The DoH grid's row is a set of named buckets — Overall, Internal, and one per channel —
 * each carrying `stock`, `drr` and a `doh` derived from them. So the same rule applies
 * bucket by bucket: stock and DRR scale and add, cover is recomputed from the sums.
 *
 * `stock: null` means the bucket has no inventory record, which is not the same as zero (the
 * grid renders it as NA). It only becomes a number once some contributor actually reports one,
 * mirroring `aggregateDrrBuckets`.
 */
const addScaledBucket = (
    target: DrrBucket,
    source: DrrBucket | undefined,
    quantity: number,
): void => {
    if (!source) return;
    target.drr += (source.drr || 0) * quantity;
    if (source.stock !== null && source.stock !== undefined) {
        target.stock = (target.stock ?? 0) + source.stock * quantity;
    }
    // Inbound units scale at the BOM ratio exactly as stock does — a parent's 10 units on
    // their way carry 10 × ratio of the child. Same null-means-no-reading rule as stock.
    if (source.in_transit !== null && source.in_transit !== undefined) {
        target.in_transit = (target.in_transit ?? 0) + source.in_transit * quantity;
    }
    // Stock Transfer is inside `stock`, so it scales with it; carried so Inv can still print
    // the shelf part (stock − stock_transfer) on an exploded row.
    if (source.stock_transfer !== null && source.stock_transfer !== undefined) {
        target.stock_transfer =
            (target.stock_transfer ?? 0) + source.stock_transfer * quantity;
    }
};

const emptyBucket = (): DrrBucket => ({
    drr: 0,
    stock: null,
    doh: null,
    in_transit: null,
    stock_transfer: null,
});

/** Cover from the bucket's own summed parts — the identical expression `aggregateDrrBuckets` uses. */
const recomputeBucket = (bucket: DrrBucket): void => {
    bucket.doh =
        bucket.stock !== null && bucket.drr > 0 ? bucket.stock / bucket.drr : null;
};

export const cloneDrrRow = (row: DrrViewRow): DrrViewRow => ({
    ...row,
    overall: { ...row.overall },
    internal: { ...row.internal },
    channels: Object.fromEntries(
        Object.entries(row.channels).map(([key, bucket]) => [key, { ...bucket }]),
    ),
    ...(row.channel_secondary && {
        channel_secondary: { ...row.channel_secondary },
    }),
});

export function addScaledDrrRow(
    target: DrrViewRow,
    source: DrrViewRow,
    quantity: number,
): void {
    addScaledBucket(target.overall, source.overall, quantity);
    addScaledBucket(target.internal, source.internal, quantity);

    for (const [key, bucket] of Object.entries(source.channels)) {
        // A parent selling on a channel the base does not adds that column, exactly as the
        // rollup does when it unions its members' channel keys.
        target.channels[key] ??= emptyBucket();
        addScaledBucket(target.channels[key], bucket, quantity);
    }

    if (source.channel_secondary) {
        target.channel_secondary ??= emptyBucket();
        addScaledBucket(target.channel_secondary, source.channel_secondary, quantity);
    }
}

export function recomputeDrrRow(row: DrrViewRow): void {
    recomputeBucket(row.overall);
    recomputeBucket(row.internal);
    for (const bucket of Object.values(row.channels)) recomputeBucket(bucket);
    if (row.channel_secondary) recomputeBucket(row.channel_secondary);
}

export function makeEmptyDrrRow(edge: ProductRelationEdge): DrrViewRow {
    return {
        product_id: edge.childId,
        sku: edge.childSku ?? edge.childId,
        name: edge.childName ?? '',
        category: edge.childCategory,
        tier: edge.childTier,
        product_group: edge.childProductGroup,
        is_active: null,
        overall: emptyBucket(),
        internal: emptyBucket(),
        channels: {},
    };
}

/** This grid keys on snake_case `product_id`, unlike the other two. */
export const getDrrRowId = (row: DrrViewRow): string => row.product_id;
